import axios from 'axios';
import { BASE_URL, DEFAULT_HEADERS } from '@constants/apiEndpoints';
import apiRequestConfig from '@interfaces/apiEndpoints.interface';
import { handleCostcoVerificationSSO } from '@src/utils/handleCostcoVerificationSso';
import { handleCostcoAdminSSO } from '@src/utils/handleCostcoAdminSso';

// Create Axios instance
const httpClient = axios.create({
  headers: {
    ...DEFAULT_HEADERS,
  },
});

httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Error in API call:', error);

    const requestUrl = error.config?.url || '';
    const method = error.config?.method?.toUpperCase() || '';
    const isUnauthorized = error.response?.status === 401;
    const isForbidden = error.response?.status === 403;

    //Detect costco admin dashboard API
    const isCostcoAdminRequest =
      method === 'GET' &&
      (requestUrl.includes('/membership-verification?page=') ||
        requestUrl.includes('/membership-verification/search') ||
        requestUrl.includes('/membership-verification/download'));

    // Detect Costco Member Verification API
    const isCostcoVerificationRequest =
      method === 'POST' && requestUrl.endsWith('/membership-verification');

    // Costco Member Verification specific 403 handling
    if (isCostcoVerificationRequest && (isForbidden || isUnauthorized)) {
      handleCostcoVerificationSSO();
      return Promise.reject(error);
    }

    // Costco Admin Dashboard specific 403 handling
    if (isCostcoAdminRequest && (isForbidden || isUnauthorized)) {
      handleCostcoAdminSSO();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

/**
 * Generic API call function
 * @param payload - Request body data (if applicable)
 * @param requestConfig - API configuration object (path and method)
 * @parametersOrQuery - Request query parameters (if applicable)
 * @returns API response data
 */
const makeApiRequest = async (
  payload = {},
  requestConfig: apiRequestConfig,
  parametersOrQuery: string,
  customHeaders: Record<string, string> = {}
) => {
  try {
    let response;
    const url =
      typeof requestConfig.url === 'function'
        ? requestConfig.url(parametersOrQuery)
        : requestConfig.url;

    switch (requestConfig.method) {
      case 'GET':
        response = await httpClient.get(url, {
          headers: { ...customHeaders },
        });
        break;
      case 'POST':
        response = await httpClient.post(url, payload, {
          headers: { ...customHeaders },
        });
        break;
      case 'PUT':
        response = await httpClient.put(url, payload, {
          headers: { ...customHeaders },
        });
        break;
      case 'DELETE':
        response = await httpClient.delete(url, {
          headers: { ...customHeaders },
        });
        break;
      case 'PATCH':
        response = await httpClient.patch(url, payload, {
          headers: { ...customHeaders },
        });
        break;
      default:
        throw new Error(`Unsupported request method: ${requestConfig.method}`);
    }

    return response;
  } catch (error) {
    console.error(`Error in API call: ${requestConfig.url}`, error);
    throw error;
  }
};

export default makeApiRequest;
